import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ZoneMarket.css';

// Zone market — totals from the myipl market data, by zone (Kohinoor only).
const n = (v) => (v === null || v === undefined ? 0 : Number(v));
const fmt = (v) => (v ? n(v).toLocaleString('en-IN') : '—');
const inr = (v) => {
  const x = n(v);
  if (!x) return '—';
  if (x >= 1e7) return `₹${(x / 1e7).toFixed(x >= 1e8 ? 0 : 2)} Cr`;
  if (x >= 1e5) return `₹${(x / 1e5).toFixed(x >= 1e6 ? 0 : 1)} L`;
  return `₹${x.toLocaleString('en-IN')}`;
};
const psf = (v) => (v ? `₹${n(v).toLocaleString('en-IN')}` : '—');

const wavg = (rows, val, w) => {
  let s = 0, t = 0;
  rows.forEach((r) => { if (r[val] && r[w]) { s += n(r[val]) * n(r[w]); t += n(r[w]); } });
  return t ? Math.round(s / t) : null;
};

const ZoneMarket = ({ apiBase, zones }) => {
  const [zone, setZone] = useState(zones[0]?.zone || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!zone) return;
    let live = true;
    setLoading(true);
    setError(null);
    axios.get(`${apiBase}/zone-market`, { params: { zone } })
      .then((res) => { if (live) setData(res.data); })
      .catch(() => { if (live) setError('Could not load market data for this zone.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [apiBase, zone]);

  const rows = data?.localities || [];
  const sum = (k) => rows.reduce((a, r) => a + n(r[k]), 0);
  const active = rows.filter((r) => n(r.sale_n) + n(r.rent_n) + n(r.buy_n) + n(r.rentreq_n) + n(r.deeds) > 0);
  const shown = showAll ? rows : active;

  const saleN = sum('sale_n'), rentN = sum('rent_n');
  const buyN = sum('buy_n'), rentReqN = sum('rentreq_n');
  const bhk = [['1 BHK', sum('bhk1')], ['2 BHK', sum('bhk2')], ['3 BHK', sum('bhk3')], ['4+ BHK', sum('bhk4')]];
  const bhkTotal = bhk.reduce((a, b) => a + b[1], 0);
  const units = sum('units'), sold = sum('sold');
  const deedsUntil = data?.deeds_until
    ? new Date(data.deeds_until).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
    : null;

  return (
    <div className="zm">
      <div className="zm-bar">
        <label htmlFor="zm-zone">Zone</label>
        <select id="zm-zone" value={zone} onChange={(e) => setZone(e.target.value)}>
          {zones.map((z) => (
            <option key={z.zone} value={z.zone}>{z.zone}</option>
          ))}
        </select>
        <span className="zm-note">Totals from live market data · no individual listings or names</span>
      </div>

      {error && <div className="zm-error">{error}</div>}
      {loading && !data && <div className="zm-loading">Loading {zone}…</div>}

      {data && (
        <div className={loading ? 'zm-body zm-dim' : 'zm-body'}>
          <div className="zm-tiles">
            <div className="zm-tile">
              <div className="zm-k">Homes for sale</div>
              <div className="zm-v">{fmt(saleN)}</div>
              <div className="zm-s">Median {psf(wavg(rows, 'sale_psf', 'sale_n'))} / sq ft carpet</div>
            </div>
            <div className="zm-tile">
              <div className="zm-k">Homes for rent</div>
              <div className="zm-v">{fmt(rentN)}</div>
              <div className="zm-s">Typical rent {inr(wavg(rows, 'rent_med', 'rent_n'))} / month</div>
            </div>
            <div className="zm-tile">
              <div className="zm-k">Active buyers · 90 days</div>
              <div className="zm-v">{fmt(buyN)}</div>
              <div className="zm-s">{fmt(rentReqN)} tenants looking</div>
            </div>
            <div className="zm-tile">
              <div className="zm-k">RERA projects under way</div>
              <div className="zm-v">{fmt(sum('proj_n'))}</div>
              <div className="zm-s">{fmt(units - sold)} of {fmt(units)} units unsold</div>
            </div>
            <div className="zm-tile">
              <div className="zm-k">Due in the next 2 years</div>
              <div className="zm-v">{fmt(sum('poss_2y'))}</div>
              <div className="zm-s">projects · {fmt(sum('unsold_2y'))} units still unsold</div>
            </div>
            <div className="zm-tile">
              <div className="zm-k">Registered sale deeds</div>
              <div className="zm-v">{fmt(sum('deeds'))}</div>
              <div className="zm-s">Median {psf(wavg(rows, 'deed_psf', 'deeds'))} / sq ft{deedsUntil ? ` · 12 months to ${deedsUntil}` : ''}</div>
            </div>
          </div>

          {bhkTotal > 0 && (
            <div className="zm-mix">
              <div className="zm-mix-title">What's on offer (residential listings by size)</div>
              <div className="zm-mix-bar">
                {bhk.map(([k, v], i) => v > 0 && (
                  <div key={k} className={`zm-seg zm-seg${i}`} style={{ flexGrow: v }} title={`${k}: ${fmt(v)}`} />
                ))}
              </div>
              <div className="zm-mix-legend">
                {bhk.map(([k, v], i) => (
                  <span key={k}><i className={`zm-dot zm-seg${i}`} />{k} {bhkTotal ? Math.round((v * 100) / bhkTotal) : 0}%</span>
                ))}
              </div>
            </div>
          )}

          <div className="zm-table-wrap">
            <table className="zm-table">
              <thead>
                <tr>
                  <th>Locality</th>
                  <th>For sale</th>
                  <th>Median ₹/sq ft</th>
                  <th>Median ask</th>
                  <th>For rent</th>
                  <th>Typical rent</th>
                  <th>Buyers (90 d)</th>
                  <th>Median budget</th>
                  <th>Tenants (90 d)</th>
                  <th>RERA projects</th>
                  <th>Unsold units</th>
                  <th>Deeds</th>
                  <th>Deed ₹/sq ft</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.locality}>
                    <td className="zm-loc">{r.locality}</td>
                    <td>{fmt(r.sale_n)}</td>
                    <td>{psf(r.sale_psf)}</td>
                    <td>{inr(r.sale_price)}</td>
                    <td>{fmt(r.rent_n)}</td>
                    <td>{inr(r.rent_med)}</td>
                    <td>{fmt(r.buy_n)}</td>
                    <td>{inr(r.buy_budget)}</td>
                    <td>{fmt(r.rentreq_n)}</td>
                    <td>{fmt(r.proj_n)}</td>
                    <td>{r.units ? fmt(Math.max(n(r.units) - n(r.sold), 0)) : '—'}</td>
                    <td>{fmt(r.deeds)}</td>
                    <td>{psf(r.deed_psf)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > active.length && (
            <button className="zm-more" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Hide localities with no listings or demand' : `Show all ${rows.length} localities`}
            </button>
          )}

          <p className="zm-foot">
            Listings are live published listings. Buyers and tenants are client requirements from the last 90 days.
            RERA projects are new and ongoing registrations; a project is counted in one locality only.
            Sale-deed figures come from registration records{deedsUntil ? `, which currently run to ${deedsUntil}` : ''}.
            All figures are totals and medians; refreshed every 30 minutes.
          </p>
        </div>
      )}
    </div>
  );
};

export default ZoneMarket;
